import { Injectable, signal, computed, inject } from '@angular/core';
import { interval, Subject } from 'rxjs';
import { ToastService } from './toast.service';
import { type Notification } from '@components/ui/notification-item.component';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private toastService = inject(ToastService);

  // Signal para las notificaciones
  private _notifications = signal<Notification[]>([]);

  // Computed para notificaciones no leídas
  public notifications = this._notifications.asReadonly();
  public unreadCount = computed(() => this._notifications().filter((n) => !n.isRead).length);

  // Subject para eventos de notificaciones
  private notificationReceived = new Subject<Notification>();

  // Tipos de notificaciones para simular
  private notificationTypes: {
    type: 'info' | 'success' | 'warning' | 'error';
    titles: string[];
    messages: string[];
  }[] = [
    {
      type: 'info',
      titles: ['Nueva actualización disponible', 'Información del sistema', 'Recordatorio'],
      messages: [
        'Se ha lanzado una nueva versión de la aplicación',
        'El mantenimiento está programado para el próximo fin de semana',
        'Revisa tu configuración de perfil',
      ],
    },
    {
      type: 'success',
      titles: ['Operación exitosa', 'Proceso completado', 'Tarea finalizada'],
      messages: [
        'El backup de datos se completó correctamente',
        'Usuario creado exitosamente',
        'Configuración guardada correctamente',
      ],
    },
    {
      type: 'warning',
      titles: ['Advertencia del sistema', 'Atención requerida', 'Verificación necesaria'],
      messages: [
        'El espacio de almacenamiento está llegando al 80%',
        'Algunos servicios pueden experimentar lentitud',
        'Revisa las credenciales de acceso',
      ],
    },
    {
      type: 'error',
      titles: ['Error crítico', 'Fallo del sistema', 'Problema detectado'],
      messages: [
        'Error en la conexión con la base de datos',
        'Fallo en el procesamiento de pagos',
        'No se pudo completar la operación solicitada',
      ],
    },
  ];

  constructor() {
    // Inicializar con algunas notificaciones de ejemplo
    this.initializeNotifications();

    // Simular recepción de notificaciones cada 5 segundos
    // this.startNotificationSimulation();
  }

  private initializeNotifications(): void {
    const initialNotifications: Notification[] = [
      {
        id: '1',
        title: 'Bienvenido al sistema',
        message: 'Tu cuenta ha sido configurada correctamente',
        type: 'success',
        timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000), // 2 horas atrás
        isRead: false,
        avatar: undefined,
        actionText: 'Ver perfil',
        actionUrl: '/profile',
      },
      {
        id: '2',
        title: 'Mantenimiento programado',
        message: 'El sistema estará en mantenimiento el próximo domingo de 2:00 AM a 4:00 AM',
        type: 'warning',
        timestamp: new Date(Date.now() - 30 * 60 * 1000), // 30 minutos atrás
        isRead: true,
        avatar: undefined,
      },
    ];

    this._notifications.set(initialNotifications);
  }

  private startNotificationSimulation(): void {
    // Simular recepción de notificaciones cada 5 segundos
    interval(5000).subscribe(() => {
      this.simulateIncomingNotification();
    });
  }

  private simulateIncomingNotification(): void {
    const randomType = this.notificationTypes[Math.floor(Math.random() * this.notificationTypes.length)];
    const randomTitle = randomType.titles[Math.floor(Math.random() * randomType.titles.length)];
    const randomMessage = randomType.messages[Math.floor(Math.random() * randomType.messages.length)];

    const newNotification: Notification = {
      id: this.generateId(),
      title: randomTitle,
      message: randomMessage,
      type: randomType.type,
      timestamp: new Date(),
      isRead: false,
      avatar: undefined,
      actionText: this.getActionText(randomType.type),
      actionUrl: this.getActionUrl(randomType.type),
    };

    // Agregar la notificación al estado
    this.addNotification(newNotification);

    // Mostrar toast
    this.showToast(newNotification);
  }

  private getActionText(type: string): string | undefined {
    const actions: Record<string, string> = {
      info: 'Ver detalles',
      success: 'Continuar',
      warning: 'Revisar',
      error: 'Corregir',
    };
    return Math.random() > 0.5 ? actions[type] : undefined;
  }

  private getActionUrl(type: string): string | undefined {
    const urls: Record<string, string> = {
      info: '/tasks',
      success: '/tasks',
      warning: '/configuration',
      error: '/logs',
    };
    return Math.random() > 0.5 ? urls[type] : undefined;
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  private showToast(notification: Notification): void {
    const message = `${notification.title}: ${notification.message}`;

    switch (notification.type) {
      case 'success':
        this.toastService.success(message, 5000);
        break;
      case 'error':
        this.toastService.error(message, 8000);
        break;
      case 'warning':
        this.toastService.warning(message, 6000);
        break;
      case 'info':
      default:
        this.toastService.info(message, 5000);
        break;
    }
  }

  // Métodos públicos para gestionar notificaciones
  addNotification(notification: Notification): void {
    this._notifications.update((notifications) => [notification, ...notifications]);
  }

  markAsRead(notificationId: string): void {
    this._notifications.update((notifications) =>
      notifications.map((n) => (n.id === notificationId ? { ...n, isRead: true } : n)),
    );
  }

  markAllAsRead(): void {
    this._notifications.update((notifications) => notifications.map((n) => ({ ...n, isRead: true })));
  }

  removeNotification(notificationId: string): void {
    this._notifications.update((notifications) => notifications.filter((n) => n.id !== notificationId));
  }

  clearAllNotifications(): void {
    this._notifications.set([]);
  }

  getNotificationById(id: string): Notification | undefined {
    return this._notifications().find((n) => n.id === id);
  }

  getNotificationsByType(type: string): Notification[] {
    return this._notifications().filter((n) => n.type === type);
  }

  getUnreadNotifications(): Notification[] {
    return this._notifications().filter((n) => !n.isRead);
  }
}
